import React, { createContext, useCallback, useContext, useState } from "react";
import { Check, X as XIcon } from "lucide-react";
import useText from "hooks/useText";

// Same look as the react-toastify toasts of app.admin: bottom left, two at most,
// dark grey, icon in a coloured dot, close cross and a bar that empties in 5 s
// (paused on hover). A click closes it. `action` ({ label, onClick }) adds a
// gesture such as "Undo".
const ToastContext = createContext(() => {});
export const useToast = () => useContext(ToastContext);

const TOAST_TONES = {
  success: {
    color: "bg-[#07bc0c]",
    mark: <Check size={13} strokeWidth={3.5} className="text-[#374151]" />,
  },
  error: {
    color: "bg-[#e74c3c]",
    mark: (
      <span className="text-[13px] font-extrabold leading-none text-[#374151]">
        !
      </span>
    ),
  },
};

const ToastProvider = ({ children }) => {
  const { text } = useText("crmUi");
  const [toasts, setToasts] = useState([]);
  const close = useCallback(
    (id) => setToasts((list) => list.filter((t) => t.id !== id)),
    []
  );
  const show = useCallback((message, tone = "success", action = null) => {
    const id = `${Date.now()}-${Math.random()}`;
    setToasts((list) => [...list, { id, text: message, tone, action }].slice(-2));
  }, []);
  return (
    <ToastContext.Provider value={show}>
      {children}
      <div className="pointer-events-none fixed bottom-4 left-4 z-[60] flex w-80 flex-col gap-2">
        {toasts.map((t) => {
          const tone = TOAST_TONES[t.tone] ?? TOAST_TONES.success;
          return (
            <div
              key={t.id}
              role="status"
              onClick={() => close(t.id)}
              className="toast-in group pointer-events-auto relative flex min-h-16 cursor-pointer items-center gap-2.5 overflow-hidden rounded bg-[#374151] py-2 pl-3 pr-8 text-sm leading-5 text-white shadow-[0_1px_10px_0_rgba(0,0,0,0.1),0_2px_15px_0_rgba(0,0,0,0.05)]"
            >
              <span
                className={`flex size-5 shrink-0 items-center justify-center rounded-full ${tone.color}`}
              >
                {tone.mark}
              </span>
              <span className="min-w-0 flex-1 py-1.5">
                {t.text}
                {t.action && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      close(t.id);
                      t.action.onClick();
                    }}
                    className="ml-2 font-semibold text-primary underline-offset-2 hover:underline"
                  >
                    {t.action.label}
                  </button>
                )}
              </span>
              <button
                type="button"
                aria-label={text("close")}
                onClick={(e) => {
                  e.stopPropagation();
                  close(t.id);
                }}
                className="absolute right-1.5 top-1.5 flex size-5 items-center justify-center rounded text-[#9CA3AF] transition-colors hover:text-white"
              >
                <XIcon size={14} strokeWidth={2.5} />
              </button>
              <span
                onAnimationEnd={() => close(t.id)}
                className={`toast-progress absolute bottom-0 left-0 h-[5px] w-full origin-left opacity-70 group-hover:[animation-play-state:paused] ${tone.color}`}
              />
            </div>
          );
        })}
      </div>
    </ToastContext.Provider>
  );
};

export default ToastProvider;
